/**
 * Firebase Client Config — GenesisBox
 *
 * Project: genesisbox-2261b
 * Hosting: box.genesisframeworks.com (Firebase Hosting)
 *
 * The `apiKey` here is PUBLIC by design — Firebase secures your app via
 * Firestore Security Rules and App Check, NOT by hiding this key.
 * https://firebase.google.com/docs/projects/api-keys
 *
 * We still allow overrides via VITE_FIREBASE_* env vars so staging / preview
 * channels can target a different Firebase project without code changes.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type Auth,
  type User as FirebaseUser,
} from 'firebase/auth';

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyBSYtkYOFfLxrXE9VVmrDRqSV3Dw8yFINg',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'genesisbox-2261b.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'genesisbox-2261b',
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'genesisbox-2261b.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '641638422189',
  appId:
    import.meta.env.VITE_FIREBASE_APP_ID || '1:641638422189:web:543f667066878660de5824',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-04M1Y53NH7',
};

// Initialize once. Vite's HMR may re-import this file; guard against double-init.
let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (!_app) _app = initializeApp(firebaseConfig);
  return _app;
}

export function getFirebaseAuth(): Auth {
  if (!_auth) _auth = getAuth(getFirebaseApp());
  return _auth;
}

function createGoogleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

function createMicrosoftProvider(): OAuthProvider {
  const provider = new OAuthProvider('microsoft.com');
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

async function signInWithProvider(
  provider: GoogleAuthProvider | OAuthProvider,
): Promise<{ idToken: string; user: FirebaseUser }> {
  const auth = getFirebaseAuth();
  try {
    const result = await signInWithPopup(auth, provider);
    const idToken = await result.user.getIdToken();
    return { idToken, user: result.user };
  } catch (err: any) {
    if (
      err?.code === 'auth/popup-blocked'
      || err?.code === 'auth/popup-closed-by-user'
      || err?.code === 'auth/cancelled-popup-request'
    ) {
      await signInWithRedirect(auth, provider);
      return new Promise(() => {});
    }
    throw err;
  }
}

/**
 * Sign in with Google. Tries popup first (desktop); falls back to full-page
 * redirect when the popup is blocked (mobile browsers, in-app browsers).
 *
 * When redirect is used the function navigates away and never resolves —
 * call checkGoogleRedirectResult() on app load to complete the flow.
 */
export async function signInWithGoogle(): Promise<{ idToken: string; user: FirebaseUser }> {
  return signInWithProvider(createGoogleProvider());
}

export async function signInWithMicrosoft(): Promise<{ idToken: string; user: FirebaseUser }> {
  return signInWithProvider(createMicrosoftProvider());
}

/**
 * Check whether we just returned from a Google redirect sign-in.
 * Call this once on app load (e.g. LoginPage mount). Returns the Firebase ID
 * token if a redirect result is present, or null otherwise.
 */
export async function checkGoogleRedirectResult(): Promise<{ idToken: string; user: FirebaseUser } | null> {
  try {
    const result = await getRedirectResult(getFirebaseAuth());
    if (!result) return null;
    const idToken = await result.user.getIdToken();
    return { idToken, user: result.user };
  } catch (err: any) {
    // getRedirectResult throws on auth errors (e.g. account exists with different credential).
    throw err;
  }
}

export async function firebaseSignOutAll(): Promise<void> {
  await firebaseSignOut(getFirebaseAuth());
}

export function getFirebaseAuthConfigDiagnostics(): {
  authDomain: string;
  bundleAsset: string;
} {
  function getBundleAssetName(): string {
    if (typeof document === 'undefined') return 'unknown';
    const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
    if (!script?.src) return 'dev-vite';
    try {
      return new URL(script.src, window.location.href).pathname.split('/').pop() || 'unknown';
    } catch {
      return 'unknown';
    }
  }
  return {
    authDomain: firebaseConfig.authDomain,
    bundleAsset: getBundleAssetName(),
  };
}

export { onAuthStateChanged };
export type { FirebaseUser };
